import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FileText, AlertTriangle, RefreshCw, ExternalLink, Plus, Trash2,
} from 'lucide-react';
import { resolveDealDocuments } from '@platform/shared-types';
import type {
  DealDocuments, DealDocumentSection, DealDocumentLine, DocumentKind,
  DealDocumentCatalogueEntry,
} from '@platform/shared-types';
import documentsApi from '../../services/documentsApi';
import { MultiSelectDropdown } from '../../components/MultiSelectDropdown';
import SearchableDropdown from '../../components/SearchableDropdown';

/**
 * THE DEAL'S PAPERWORK — hardware and software kept apart (2026-08-30).
 *
 * These panels are the only place a deal's documents are chosen. They replace
 * the financing wizard's per-offer agreement step, which could only ever say
 * one thing for every customer who bought a given terminal and said nothing at
 * all about the software half of the sale.
 *
 * WHAT REACHES A PICKER. Only documents that are `status: 'active'` AND carry
 * at least one PUBLISHED version — the same rule the financing wizard applied,
 * for the same reason: a draft is not a contract, and nobody may be asked to
 * sign one. Versions are deliberately NOT pinned here. The version resolves at
 * the moment of signing and freezes on the envelope then, so publishing an
 * update changes what the NEXT customer signs and nothing already signed.
 */

interface CatalogueDoc {
  documentId: string;
  title: string;
  kind: DocumentKind;
  category?: string;
}

export interface DealDocumentCatalogue {
  agreements: CatalogueDoc[];
  policies: CatalogueDoc[];
  byId: Map<string, CatalogueDoc>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  refreshing: boolean;
}

/**
 * Loads every publishable document once for the whole page.
 *
 * ONE LOAD, THREE PANELS. Hardware, software and the deal-wide extras all read
 * this — fetching per panel would triple the load.
 *
 * ONE REQUEST, NOT N+1. `GET /admin/documents` now projects each row's version
 * chain (documents-service does the per-document read server-side, pooled), so
 * "is anything published?" is answered from the list itself. This used to cost
 * a `getDocument` per document — the single biggest delay on opening a deal.
 * The per-document path is KEPT as a fallback for an older documents-service
 * whose rows carry no `versions`: the picker must not go silently empty
 * against a service that has not been redeployed yet.
 */
export function useDealDocumentCatalogue(): DealDocumentCatalogue {
  const [agreements, setAgreements] = useState<CatalogueDoc[]>([]);
  const [policies, setPolicies] = useState<CatalogueDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await documentsApi.listDocuments({ status: 'active' });
      const docs = res.documents || [];
      const entry = (d: any): CatalogueDoc => ({
        documentId: d.documentId, title: d.title, kind: d.kind, category: d.category,
      });
      const hasPublished = (versions: any): boolean =>
        Array.isArray(versions) && versions.some((v: any) => v?.status === 'published');

      // The fast path: every row already carries its chain, so nothing more is
      // fetched. Only a row with NO `versions` array at all falls back — an
      // empty array is a real answer ("nothing published"), not missing data.
      const stale = docs.filter((d: any) => !Array.isArray((d as any).versions));
      const resolved = new Map<string, boolean>(
        docs
          .filter((d: any) => Array.isArray((d as any).versions))
          .map((d: any) => [String(d.documentId), hasPublished((d as any).versions)]),
      );
      if (stale.length) {
        await Promise.all(stale.map(async (d: any) => {
          try {
            const detail = await documentsApi.getDocument(d.documentId);
            resolved.set(String(d.documentId), hasPublished(detail.versions));
          } catch {
            // One unreadable document must not blank the whole picker — it is
            // simply not offered, exactly as an unpublished one is not.
            resolved.set(String(d.documentId), false);
          }
        }));
      }

      const usable = docs
        .filter((d: any) => resolved.get(String(d.documentId)))
        .map(entry);
      const byTitle = (a: CatalogueDoc, b: CatalogueDoc) => a.title.localeCompare(b.title);
      setAgreements(usable.filter((d) => d.kind === 'agreement').sort(byTitle));
      setPolicies(usable.filter((d) => d.kind === 'policy').sort(byTitle));
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load documents from documents-service');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await load();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [load]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }, [load]);

  const byId = useMemo(
    () => new Map([...agreements, ...policies].map((d) => [d.documentId, d])),
    [agreements, policies],
  );

  return { agreements, policies, byId, loading, error, refresh, refreshing };
}

const optionsFrom = (docs: CatalogueDoc[]) =>
  docs.map((d) => ({ value: d.documentId, label: d.title, subtitle: d.category || 'Uncategorised' }));

const LIGHT_PANEL = 'rounded-2xl border border-stone-200 bg-white text-stone-800';
const LIGHT_HEAD = 'font-mono text-[10px] tracking-[.14em] uppercase text-stone-400';
const LIGHT_LABEL = 'flex items-center gap-2 text-[11px] font-medium text-stone-600 mb-1';

/** Opens Legal → Documents in a new tab; the deal's own state is untouched. */
const BrowseButtons: React.FC<{ catalogue: DealDocumentCatalogue }> = ({ catalogue }) => (
  <div className="mt-3 flex flex-wrap items-center gap-2">
    <button
      type="button"
      onClick={() => window.open('/legal/documents', '_blank', 'noopener')}
      className="flex items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-stone-700 hover:bg-stone-50"
      title="Opens the document library in a new tab — draft and publish there, then Refresh"
    >
      <ExternalLink size={12} /> Browse documents
    </button>
    <button
      type="button"
      onClick={catalogue.refresh}
      disabled={catalogue.refreshing}
      className="flex items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-50"
      title="Re-fetch published documents"
    >
      {catalogue.refreshing ? <RubyGemMark size={12} drawing color="currentColor" /> : <RefreshCw size={12} />} Refresh
    </button>
  </div>
);

const CatalogueProblem: React.FC<{ catalogue: DealDocumentCatalogue }> = ({ catalogue }) => {
  if (catalogue.loading) {
    return (
      <p className="flex items-center gap-2 text-[11.5px] text-stone-500">
        <RubyGemMark size={13} drawing /> Loading published documents…
      </p>
    );
  }
  if (catalogue.error) {
    return (
      <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-[11.5px] text-red-800">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        {catalogue.error} — nothing can be selected until this loads. Anything already saved on the deal is
        untouched.
      </div>
    );
  }
  if (!catalogue.agreements.length && !catalogue.policies.length) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-[11.5px] text-amber-900">
        No published documents exist yet. Use <b>Browse documents</b> to draft and publish one in a new tab, then
        hit <b>Refresh</b>.
      </div>
    );
  }
  return null;
};

/**
 * ONE SECTION — hardware or software. Two pickers: agreements are signed,
 * policies are accepted.
 */
export const DealPaperworkSection: React.FC<{
  section: DealDocumentSection;
  title: string;
  hint: string;
  value: DealDocuments;
  onChange: (next: DealDocuments) => void;
  catalogue: DealDocumentCatalogue;
}> = ({ section, title, hint, value, onChange, catalogue }) => {
  const cfg = value[section] || { agreementDocumentIds: [], policyDocumentIds: [] };
  const setCfg = (patch: Partial<typeof cfg>) => onChange({
    ...value,
    [section]: {
      agreementDocumentIds: cfg.agreementDocumentIds || [],
      policyDocumentIds: cfg.policyDocumentIds || [],
      ...patch,
    },
  });
  const problem = <CatalogueProblem catalogue={catalogue} />;
  const disabled = catalogue.loading || Boolean(catalogue.error);

  return (
    <div className={`${LIGHT_PANEL} p-4 space-y-4`}>
      <div>
        <span className={`${LIGHT_HEAD} flex items-center gap-2`}>
          <FileText className="w-3 h-3" /> {title}
        </span>
        <p className="mt-1 text-[11.5px] text-stone-500">{hint}</p>
      </div>

      {problem}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className={LIGHT_LABEL}>Agreements to sign</label>
          <MultiSelectDropdown
            options={optionsFrom(catalogue.agreements)}
            value={cfg.agreementDocumentIds || []}
            onChange={(ids) => setCfg({ agreementDocumentIds: ids })}
            disabled={disabled}
            placeholder="-- Select the agreements to be signed --"
            searchPlaceholder="Search agreements…"
            summarize={(sel) => (sel.length === 1 ? sel[0].label : `${sel.length} agreements`)}
          />
          <p className="mt-1 text-[11px] text-stone-500">
            Signed before this deal can be fulfilled.
          </p>
        </div>
        <div>
          <label className={LIGHT_LABEL}>Policies to attach</label>
          <MultiSelectDropdown
            options={optionsFrom(catalogue.policies)}
            value={cfg.policyDocumentIds || []}
            onChange={(ids) => setCfg({ policyDocumentIds: ids })}
            disabled={disabled}
            placeholder="-- Select policies --"
            searchPlaceholder="Search policies…"
            summarize={(sel) => (sel.length === 1 ? sel[0].label : `${sel.length} policies`)}
          />
          <p className="mt-1 text-[11px] text-stone-500">
            Disclosed and accepted, never counter-signed.
          </p>
        </div>
      </div>

      <BrowseButtons catalogue={catalogue} />
    </div>
  );
};

let lineSeq = 0;
const newLineId = () => `line_${Date.now().toString(36)}_${(lineSeq += 1).toString(36)}`;

/**
 * DEAL-WIDE — the return/refund policy, and lines the operator names
 * themselves.
 *
 * A custom line's `label` is what the customer reads at checkout and the
 * `documentId` is what they actually sign; they are separate on purpose, so
 * renaming a line on one deal never renames the document under every other
 * deal using it.
 */
export const DealDocumentExtras: React.FC<{
  value: DealDocuments;
  onChange: (next: DealDocuments) => void;
  catalogue: DealDocumentCatalogue;
}> = ({ value, onChange, catalogue }) => {
  const lines = value.customLines || [];
  const disabled = catalogue.loading || Boolean(catalogue.error);

  const setLines = (next: DealDocumentLine[]) => onChange({ ...value, customLines: next });
  const patchLine = (lineId: string, patch: Partial<DealDocumentLine>) =>
    setLines(lines.map((l) => (l.lineId === lineId ? { ...l, ...patch } : l)));
  const addLine = (kind: DocumentKind) => setLines([
    ...lines,
    { lineId: newLineId(), label: '', kind, documentId: '', section: 'hardware', required: true },
  ]);

  const docsFor = (kind: DocumentKind) => (kind === 'agreement' ? catalogue.agreements : catalogue.policies);

  return (
    <div className={`${LIGHT_PANEL} p-4 space-y-4`}>
      <div>
        <span className={`${LIGHT_HEAD} flex items-center gap-2`}>
          <FileText className="w-3 h-3" /> Deal-wide paperwork
        </span>
        <p className="mt-1 text-[11.5px] text-stone-500">
          Terms that span both halves of the sale, and any line this deal needs that the two sections above do not
          cover.
        </p>
      </div>

      <CatalogueProblem catalogue={catalogue} />

      <div className="max-w-md">
        <label className={LIGHT_LABEL}>Return &amp; refund policy</label>
        <SearchableDropdown
          options={optionsFrom(catalogue.policies)}
          value={value.returnRefundPolicyDocumentId || null}
          onChange={(v) => onChange({ ...value, returnRefundPolicyDocumentId: v || undefined })}
          disabled={disabled}
          placeholder="-- Select the return & refund policy --"
          variant="light"
        />
        <p className="mt-1 text-[11px] text-stone-500">
          Covers hardware and software alike, so it sits under neither section.
        </p>
      </div>

      <div>
        <label className={LIGHT_LABEL}>
          Custom lines
          <span className="font-normal text-stone-400">— the name you give it is what the customer sees</span>
        </label>

        {lines.length === 0 ? (
          <p className="rounded-xl border border-dashed border-stone-300 bg-stone-50 px-3 py-4 text-[11.5px] text-stone-500">
            None. Add one when this deal needs a document the standard sections do not carry — a site exclusivity
            rider, a compliance undertaking.
          </p>
        ) : (
          <div className="space-y-2">
            {lines.map((line) => (
              <div key={line.lineId} className="rounded-xl border border-stone-200 bg-stone-50/60 p-3">
                <div className="grid grid-cols-1 gap-2 md:grid-cols-12">
                  <div className="md:col-span-4">
                    <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-stone-400">
                      Shown at checkout as
                    </label>
                    <input
                      value={line.label}
                      onChange={(e) => patchLine(line.lineId, { label: e.target.value })}
                      placeholder="e.g. Buffalo site exclusivity"
                      className="w-full rounded-lg border border-stone-300 bg-white px-2.5 py-2 text-xs text-stone-800"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-stone-400">
                      Type
                    </label>
                    <SearchableDropdown
                      options={[
                        { value: 'agreement', label: 'Agreement' },
                        { value: 'policy', label: 'Policy' },
                      ]}
                      value={line.kind}
                      onChange={(v) => {
                        const kind = (v || 'agreement') as DocumentKind;
                        // The chosen document belongs to the OLD kind's list, so
                        // it is cleared rather than left pointing at something
                        // this line can no longer offer.
                        patchLine(line.lineId, { kind, documentId: '' });
                      }}
                      variant="light"
                      compact
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-stone-400">
                      Document
                    </label>
                    <SearchableDropdown
                      options={optionsFrom(docsFor(line.kind))}
                      value={line.documentId || null}
                      onChange={(v) => patchLine(line.lineId, { documentId: v || '' })}
                      disabled={disabled}
                      placeholder="-- Choose --"
                      variant="light"
                      compact
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-stone-400">
                      Section
                    </label>
                    <SearchableDropdown
                      options={[
                        { value: 'hardware', label: 'Hardware' },
                        { value: 'software', label: 'Software' },
                      ]}
                      value={line.section}
                      onChange={(v) => patchLine(line.lineId, { section: (v || 'hardware') as DealDocumentSection })}
                      variant="light"
                      compact
                    />
                  </div>
                  <div className="flex items-end md:col-span-1">
                    <button
                      type="button"
                      onClick={() => setLines(lines.filter((l) => l.lineId !== line.lineId))}
                      className="mb-0.5 rounded-lg border border-stone-300 bg-white p-2 text-stone-400 hover:border-red-300 hover:text-red-600"
                      title="Remove this line"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                <label className="mt-2 flex items-center gap-2 text-[11px] text-stone-600">
                  <input
                    type="checkbox"
                    checked={line.required !== false}
                    onChange={(e) => patchLine(line.lineId, { required: e.target.checked })}
                    className="h-3.5 w-3.5 accent-red-600"
                  />
                  Required — checkout cannot complete until this is discharged
                </label>
              </div>
            ))}
          </div>
        )}

        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => addLine('agreement')}
            className="flex items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-stone-700 hover:bg-stone-50"
          >
            <Plus size={12} /> Add agreement line
          </button>
          <button
            type="button"
            onClick={() => addLine('policy')}
            className="flex items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-stone-700 hover:bg-stone-50"
          >
            <Plus size={12} /> Add policy line
          </button>
        </div>
      </div>

      <BrowseButtons catalogue={catalogue} />
    </div>
  );
};

/**
 * WHAT THE TENANT ACTUALLY SEES — the deal's binder flattened by the SAME
 * `resolveDealDocuments` checkout resolves with, so this is a preview of the
 * real thing rather than a second implementation that agrees until it does not.
 *
 * It is also the only place a MISTAKE becomes visible before a customer hits
 * it: a document that has since been retired, or one a picker still names but
 * the library no longer has, is dropped by the resolver and called out here by
 * id instead of quietly going missing at checkout.
 */
export const DealDocumentPreview: React.FC<{
  value: DealDocuments;
  catalogue: DealDocumentCatalogue;
}> = ({ value, catalogue }) => {
  const entries: DealDocumentCatalogueEntry[] = useMemo(
    () => [...catalogue.agreements, ...catalogue.policies].map((d) => ({
      documentId: d.documentId,
      kind: d.kind,
      status: 'active' as const,
      title: d.title,
    })),
    [catalogue.agreements, catalogue.policies],
  );

  const { items, dropped } = useMemo(
    () => resolveDealDocuments(value, entries),
    [value, entries],
  );

  if (catalogue.loading || catalogue.error) return null;

  return (
    <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
      <span className={`${LIGHT_HEAD} flex items-center gap-2`}>
        <FileText className="w-3 h-3" /> What the tenant sees at checkout
      </span>

      {items.length === 0 ? (
        <p className="mt-2 text-[11.5px] text-stone-500">
          Nothing yet. With no paperwork on this deal, a checkout falls back to whatever documents the device
          offers themselves carry.
        </p>
      ) : (
        <div className="mt-2 overflow-hidden rounded-xl border border-stone-200 bg-white">
          {items.map((item) => (
            <div
              key={item.documentId}
              className="flex flex-wrap items-center gap-2 border-b border-stone-100 px-3 py-2 text-[11.5px] last:border-b-0"
            >
              <span className="text-stone-400">{item.kind === 'agreement' ? '\u2610' : '\ud83d\udcc4'}</span>
              <span className="font-semibold text-stone-800">{item.label}</span>
              <span className="text-stone-400">
                {item.kind === 'agreement' ? '\u2014 sign' : '\u2014 accept'}
                {item.required ? '' : ' (optional)'}
              </span>
              <span className="ml-auto text-[10px] uppercase tracking-wide text-stone-400">{item.section}</span>
            </div>
          ))}
        </div>
      )}

      {dropped.length > 0 && (
        <div className="mt-2 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-900">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>
            {dropped.length} selected document{dropped.length === 1 ? ' is' : 's are'} retired or no longer in the
            library and will NOT be shown to the tenant: <span className="font-mono">{dropped.join(', ')}</span>.
            Replace {dropped.length === 1 ? 'it' : 'them'} above.
          </span>
        </div>
      )}
    </div>
  );
};
